import React, { useEffect } from 'react';
import { RootState, AppDispatch } from '../store';
import { useSelector, useDispatch } from 'react-redux';
import Application from '../components/Application';
import LoadingSpinner from '../components/LoadingSpinner';
import {
	Orientation,
	Platform,
	DeviceType,
	Lightbox,
	PopOver,
	WriteDirection,
	PopOverSceneItemData,
	AppView,
	SceneHelperData,
} from 'sofa-reborn-react';
import { setBrowserIsDetected } from '../store/application/actions';
import { resetScreensaverTimeout } from '../store/screensaver/actions';
import AppInitThunk from '../store/application/thunk/AppInitThunk';
import { createGlobalStyle } from 'styled-components';
import BrowserSupport from '../util/ApplicationUtils/BrowserSupport';
import { resetApplication, resetScene } from '../store/middleware/screensaverMiddleware';
import { ApplicationUtils } from '../util/ApplicationUtils';

import '@ingka/text/dist/style.css';
import '@ingka/svg-icon/dist/style.css';
import '@ingka/button/dist/style.css';
import '@ingka/image/dist/style.css';
import '@ingka/aspect-ratio-box/dist/style.css';
import '@ingka/card/dist/style.css';
import '@ingka/focus/dist/style.css';
import '@ingka/modal/dist/style.css';
import '@ingka/switch/dist/style.css';
import '@ingka/inline-message/dist/style.css';
import '@ingka/hyperlink/dist/style.css';
import '@ingka/loading/dist/style.css';

import 'focus-visible';
import { ITranslations } from '@inter-ikea-kompis/types';

declare global {
	interface Window {
		forceScreensaver;
	}
}

const ApplicationContainer: React.FC = () => {
	const orientation = useSelector<RootState, Orientation>((state) => state.deviceInfo.orientation);
	const platform = useSelector<RootState, Platform>((state) => state.deviceInfo.platform);
	const deviceType = useSelector<RootState, DeviceType>((state) => state.deviceInfo.deviceType);
	const currentLightbox = useSelector<RootState, Lightbox>(
		(state) => state.application.currentLightbox
	);
	const isInUpptackaPlatform = useSelector<RootState, boolean>(
		(state) => state.application.isInUpptackaPlatform
	);
	const writeDirection = useSelector<RootState, WriteDirection>(
		(state) => state.localization.writeDirection
	);
	const buildLimit = useSelector<RootState, PopOver>((state) => state.popOver.buildLimit);
	const disabledDrag = useSelector<RootState, PopOver>((state) => state.popOver.disabledDrag);
	const middleModuleDrag = useSelector<RootState, PopOver>(
		(state) => state.popOver.middleModuleDrag
	);
	const invalidPlacement = useSelector<RootState, PopOver>(
		(state) => state.popOver.invalidPlacement
	);
	const noValidConnection = useSelector<RootState, PopOver>(
		(state) => state.popOver.noValidConnection
	);
	const placementInformation = useSelector<RootState, PopOver>(
		(state) => state.popOver.placementInformation
	);
	const unfinishedSection = useSelector<RootState, PopOver>(
		(state) => state.popOver.unfinishedSection
	);
	const missingCoverSection = useSelector<RootState, PopOver>(
		(state) => state.popOver.missingCoverSection
	);
	const showScreensaverValue = useSelector<RootState, boolean>(
		(state) => state.screensaver.showScreensaver
	);
	const selectedSceneItem = useSelector<RootState, PopOverSceneItemData>(
		(state) => state.scene.selectedSceneItem as PopOverSceneItemData
	);
	const actionButtonsVisible = useSelector<RootState, boolean>(
		(state) => state.actionButtons.actionButtonsVisible
	);
	const appView = useSelector<RootState, AppView>((state) => state.application.appView);

	const loadingModels = useSelector<RootState, boolean>((state) => state.application.loadingModels);
	const loadingCovers = useSelector<RootState, boolean>((state) => state.application.loadingCovers);

	const showSelectAll = useSelector<RootState, boolean>(
		(state) => state.actionButtons.showSelectAll as boolean
	);

	const appInitialized = useSelector<RootState, boolean>(
		(state) => state.application.appInitialized
	);

	const sceneHelper = useSelector<RootState, SceneHelperData>(
		(state) => state.scene.sceneHelper as SceneHelperData
	);

	const browserIsDetected = useSelector<RootState, boolean>(
		(state) => state.application.browserIsDetected
	);

	const kompisTranslations = useSelector<RootState, ITranslations | null>(
		(state) => state.localization.kompisTranslations
	);

	const showFakeDataForm = useSelector<RootState, boolean | undefined>(
		(state) => state.application.showFakeDataForm
	);

	const features = useSelector<RootState, string[] | null>((state) => state.settings.features);

	const missingCover = useSelector<RootState, boolean>((state) => state.cover.missingCover);

	const GlobalStyle = createGlobalStyle`
		[data-element-name='kompis-whats-included'] {
			min-width: ${(props: {
				deviceType: DeviceType;
				orientation: Orientation;
				platform: Platform;
			}): string =>
				props.deviceType === 'mobile'
					? props.orientation === 'portrait'
						? '300px'
						: '500px'
					: props.platform === 'kiosk'
					? '700px'
					: '630px'};
		}

		* {
			font-family: 'notoIKEAlatin';
		}
	`;

	const dispatch = useDispatch<AppDispatch>();
	const onInteraction = (): void => {
		if (platform === 'kiosk') {
			dispatch(resetScreensaverTimeout());
		}
	};

	function handleTouchStartEvent(event) {
		if (!event.isTrusted) {
			return;
		}
		if (event.touches.length === 2) {
			event.preventDefault();
		}
	}

	useEffect(() => {
		const appInitThunk = new AppInitThunk();
		dispatch(appInitThunk.init());

		if (!ApplicationUtils.isProduction()) {
			window.forceScreensaver = (time = 0) => {
				setTimeout(() => {
					resetScene(dispatch);
					resetApplication(dispatch);
				}, time * 1000);
				return 'Forced screensaver';
			};
		}

		/* Prevent zooming on UPPTÄCKA kiosks SP-3411*/
		if (platform === 'kiosk') {
			document.addEventListener('touchstart', handleTouchStartEvent, {
				passive: false,
			});
			document.addEventListener('touchmove', handleTouchStartEvent, {
				passive: false,
			});
		}
	}, []);

	if (!browserIsDetected && kompisTranslations) {
		BrowserSupport.checkSupport(kompisTranslations);
		dispatch(setBrowserIsDetected(true));
	}

	return !appInitialized ? (
		<LoadingSpinner platform={platform} color={'white'} isScreenshot={false} />
	) : (
		<>
			<GlobalStyle deviceType={deviceType} orientation={orientation} platform={platform} />
			<Application
				orientation={orientation}
				platform={platform}
				deviceType={deviceType}
				writeDirection={writeDirection}
				appView={appView}
				currentLightbox={currentLightbox}
				buildLimit={buildLimit}
				disabledDrag={disabledDrag}
				middleModuleDrag={middleModuleDrag}
				invalidPlacement={invalidPlacement}
				noValidConnection={noValidConnection}
				placementInformation={placementInformation}
				unfinishedSection={unfinishedSection}
				missingCoverSection={missingCoverSection}
				selectedSceneItem={selectedSceneItem}
				actionButtonsVisible={actionButtonsVisible}
				onInteraction={onInteraction}
				showLoadingSpinner={loadingModels || loadingCovers}
				showScreensaver={showScreensaverValue}
				showSelectAll={showSelectAll}
				sceneHelper={sceneHelper}
				showFakeDataForm={showFakeDataForm}
				missingCover={missingCover}
				features={features || []}
				isInUpptackaPlatform={isInUpptackaPlatform}
			/>
		</>
	);
};

export default ApplicationContainer;
